import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Input from '@welldev-group/rcl-input';
import Icon from '@welldev-group/rcl-icon';
import Button from '@welldev-group/rcl-button';
import DatePicker from '@welldev-group/rcl-date-picker';
import { Amount, I18nText } from '@welldev-group/react-comp-lib';
import Modal from '@welldev-group/rcl-modal';
import { v4 as uuidv4 } from 'uuid';
import routes from '../../../shared/routes';
import httpClient from '../../../shared/libraries/httpClient';

const dateFormat = 'YYYY-MM-DD';

const RecordWireTransferModal = ({ invoice, isModalVisible, onModalClose }) => {
  const currentDate = moment().format(dateFormat);
  const [receivedAt, setReceivedAt] = useState(currentDate);
  const [transactionIdentifier, setTransactionIdentifier] = useState('');
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState(false);

  const recordWireTransfer = async () => {
    try {
      await httpClient.post(
        routes.billing.wireTransferPayments.create({
          affiliate_id: invoice.affiliateId,
        }),
        {
          wire_transfer_payment: {
            invoice_id: invoice.id,
            received_at: receivedAt,
            transaction_identifier: transactionIdentifier,
          },
        },
      );

      window.location.reload();
    } catch (error) {
      const response = error.response;
      if (response && response.status === 422) {
        setErrors(response.data.error);
      } else {
        throw error;
      }
    }
  };

  const onDateChangeHandler = date => {
    if (date) setReceivedAt(moment(date, 'YYYY-MM-DD').format('YYYY-MM-DD'));
  };

  return (
    <Modal
      isVisible={isModalVisible}
      onOutsideClick={onModalClose}
      className="record-wire-transfer-modal"
    >
      <div className="record-wire-transfer-modal__title">
        <Modal.Title>
          <I18nText id="billing.record_wire_transfer.confirm_payment" />
        </Modal.Title>
        <div
          onClick={onModalClose}
          onKeyDown={onModalClose}
          role="presentation"
        >
          <Icon name="close" size="normal" />
        </div>
      </div>
      <Modal.Content>
        <div className="record-wire-transfer-modal__content">
          <div className="record-wire-transfer-modal__amount">
            <div>
              <I18nText id="billing.record_wire_transfer.amount" />
              <Amount
                value={invoice.amount}
                currency={invoice.currency}
                currencyDisplay="symbol"
                className="record-wire-transfer-modal__amount--container"
              />
            </div>
            <span>{invoice.title}</span>
          </div>
          <DatePicker
            id={uuidv4()}
            focused={focused}
            initialDate={currentDate}
            required={true}
            firstDayOfWeek={0}
            futureYearsCount={0}
            pastYearsCount={0}
            enablePastDates={true}
            disableFutureDates={true}
            displayFormat="L"
            fullWidth={true}
            hideNavButtons={false}
            error={errors.receivedAt}
            date={receivedAt}
            onChange={date => onDateChangeHandler(date)}
            onFocusChange={setFocused}
            appendToBody={true}
          />
          <Input
            label={null}
            placeholder={I18n.t(
              'components.billing.record_wire_transfer.placeholders.transaction_identifier',
            )}
            required={true}
            name="transactionIdentifier"
            value={transactionIdentifier}
            onChange={value => setTransactionIdentifier(value)}
            touched={
              transactionIdentifier !== '' || !!errors.transactionIdentifier
            }
            error={errors.transactionIdentifier}
          />
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Button
          className="record-wire-transfer-modal__confirm-button"
          primary={true}
          size="small"
          label={<I18nText id="billing.record_wire_transfer.confirm_payment" />}
          onClick={recordWireTransfer}
        />
        <Button
          label={I18n.t('shared.action.cancel')}
          size="small"
          onClick={onModalClose}
        />
      </Modal.Footer>
    </Modal>
  );
};

RecordWireTransferModal.propTypes = {
  invoice: PropTypes.shape({
    id: PropTypes.number.isRequired,
    affiliateId: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    amount: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
  }).isRequired,
  isModalVisible: PropTypes.bool.isRequired,
  onModalClose: PropTypes.func.isRequired,
};

export default RecordWireTransferModal;
